/**
 * Content Plan Review — Einstein's 5-Day Morning Plan
 * Generate, approve, decline, and add notes to each content item.
 * Plans auto-generated at 7 AM daily. Manual refresh available.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Calendar, RefreshCw, CheckCircle, XCircle, Edit2, Brain,
  Clock, TrendingUp, Zap, ChevronDown, ChevronUp, MessageCircle,
  ThumbsUp, ThumbsDown, Check, X, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_COLORS = {
  LinkedIn: '#0077b5', 'Twitter/X': '#1da1f2', Instagram: '#e1306c',
  TikTok: '#ff0050', 'Blog/Article': '#a68eff', Newsletter: '#3dffa0',
  YouTube: '#fe2c55', Facebook: '#1877f2',
};

const ENGAGEMENT_COLORS = { high: '#3dffa0', medium: '#f97316', low: '#60a5fa' };

function PlanItem({ item, onAction }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(item.notes || '');
  const [modifiedTopic, setModifiedTopic] = useState(item.topic);
  const [submitting, setSubmitting] = useState(false);

  const handleAction = async (action) => {
    setSubmitting(true);
    await onAction({
      item_id: item.item_id,
      action,
      notes,
      modified_topic: editing ? modifiedTopic : null,
    });
    setSubmitting(false);
    setEditing(false);
  };

  const statusColor = {
    approve: '#3dffa0', decline: '#fe2c55', modify: '#f97316', pending: 'rgba(255,255,255,0.3)',
  }[item.status] || 'rgba(255,255,255,0.3)';

  const platformColor = PLATFORM_COLORS[item.platform] || '#a68eff';

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ border: `1px solid ${item.status !== 'pending' ? statusColor + '40' : 'rgba(255,255,255,0.08)'}`, background: 'rgba(255,255,255,0.02)' }}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Day badge */}
          <div className="w-10 h-10 rounded-xl flex flex-col items-center justify-center flex-shrink-0"
            style={{ background: `${platformColor}15`, border: `1px solid ${platformColor}30` }}>
            <div className="text-[9px] font-bold uppercase" style={{ color: platformColor }}>Day</div>
            <div className="text-sm font-black" style={{ color: platformColor }}>{item.day}</div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[9px] px-2 py-0.5 rounded-full font-bold"
                style={{ background: `${platformColor}15`, color: platformColor, border: `1px solid ${platformColor}30` }}>
                {item.platform}
              </span>
              <span className="text-[9px] px-2 py-0.5 rounded-full font-bold capitalize"
                style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)' }}>
                {item.content_type?.replace('_', ' ')}
              </span>
              <span className="text-[9px] font-bold"
                style={{ color: item.topic_type === 'primary' ? '#a68eff' : '#3dffa0' }}>
                ● {item.topic_type}
              </span>
              {item.estimated_engagement && (
                <span className="text-[9px] font-bold ml-auto"
                  style={{ color: ENGAGEMENT_COLORS[item.estimated_engagement] || '#fff' }}>
                  {item.estimated_engagement} engagement
                </span>
              )}
            </div>

            {editing ? (
              <input value={modifiedTopic} onChange={e => setModifiedTopic(e.target.value)}
                className="w-full px-2 py-1 rounded-lg text-sm font-bold outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(124,94,250,0.4)' }} />
            ) : (
              <div className="text-sm font-bold text-white leading-snug">{item.topic}</div>
            )}

            <div className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {item.date} · Best at {item.best_time || '9:00 AM'}
            </div>
          </div>

          {/* Status badge */}
          {item.status !== 'pending' && (
            <span className="flex-shrink-0 text-[10px] px-2 py-1 rounded-lg font-bold capitalize"
              style={{ background: `${statusColor}15`, color: statusColor, border: `1px solid ${statusColor}30` }}>
              {item.status === 'approve' ? 'Approved' : item.status === 'decline' ? 'Declined' : 'Modified'}
            </span>
          )}
        </div>

        {/* Einstein's Note */}
        {item.einstein_note && (
          <div className="mt-2 flex items-start gap-1.5">
            <Brain size={10} style={{ color: '#a68eff', flexShrink: 0, marginTop: 1 }} />
            <div className="text-[10px] italic" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Einstein: "{item.einstein_note}"
            </div>
          </div>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-3 space-y-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          {item.hook && (
            <div className="mt-3">
              <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#f97316' }}>Hook</div>
              <div className="text-xs italic" style={{ color: 'rgba(255,255,255,0.6)' }}>"{item.hook}"</div>
            </div>
          )}
          {item.key_points?.length > 0 && (
            <div>
              <div className="text-[9px] font-bold uppercase mb-1" style={{ color: '#60a5fa' }}>Key Points</div>
              <ul className="space-y-0.5">
                {item.key_points.map((p, i) => (
                  <li key={i} className="text-[11px] flex items-start gap-1.5" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    <span style={{ color: '#60a5fa' }}>•</span> {p}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {item.hashtags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.hashtags.map((h, i) => (
                <span key={i} className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.35)' }}>{h}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notes + Actions */}
      <div className="px-4 pb-3 space-y-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setExpanded(e => !e)}
            className="flex items-center gap-1 text-[10px] transition-all hover:opacity-70"
            style={{ color: 'rgba(255,255,255,0.3)' }}>
            {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            {expanded ? 'Less' : 'Details'}
          </button>
          <button onClick={() => setEditing(e => !e)}
            className="flex items-center gap-1 text-[10px] transition-all hover:opacity-70"
            style={{ color: editing ? '#a68eff' : 'rgba(255,255,255,0.3)' }}>
            <Edit2 size={10} /> Edit topic
          </button>
        </div>

        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Add notes or instructions (optional)..."
          rows={1}
          className="w-full px-3 py-1.5 rounded-xl text-xs outline-none resize-none"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }} />

        {item.status === 'pending' && (
          <div className="flex items-center gap-2">
            <button onClick={() => handleAction('approve')} disabled={submitting}
              data-testid={`approve-item-${item.item_id}`}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(61,255,160,0.1)', border: '1px solid rgba(61,255,160,0.3)', color: '#3dffa0' }}>
              {submitting ? <RefreshCw size={11} className="animate-spin" /> : <ThumbsUp size={11} />} Approve
            </button>
            <button onClick={() => handleAction('decline')} disabled={submitting}
              data-testid={`decline-item-${item.item_id}`}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(254,44,85,0.08)', border: '1px solid rgba(254,44,85,0.25)', color: '#fe2c55' }}>
              <ThumbsDown size={11} /> Decline
            </button>
            {(editing || notes) && (
              <button onClick={() => handleAction('modify')} disabled={submitting}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
                style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)', color: '#f97316' }}>
                <Edit2 size={11} /> Modify
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ContentPlanReview() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [days, setDays] = useState(5);

  useEffect(() => {
    fetchPlan();
  }, []);

  const fetchPlan = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/content-plan/today`);
      if (data.plan) setPlan(data.plan);
    } catch {}
    setLoading(false);
  };

  const regenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/content-plan/generate`, { days, force: true });
      if (data.plan) setPlan(data.plan);
    } catch {}
    setGenerating(false);
  };

  const handleAction = async (actionData) => {
    try {
      await axios.post(`${API}/api/content-plan/action`, actionData);
      await fetchPlan();
    } catch {}
  };

  const approveAll = async () => {
    try {
      await axios.post(`${API}/api/content-plan/approve-all`);
      await fetchPlan();
    } catch {}
  };

  const items = plan?.items || [];
  const pending = items.filter(i => i.status === 'pending').length;
  const approved = items.filter(i => i.status === 'approve').length;
  const declined = items.filter(i => i.status === 'decline').length;

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 flex-shrink-0"
        style={{ background: 'rgba(10,10,20,0.97)', borderBottom: '1px solid var(--llmc-border)' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(124,94,250,0.3), rgba(166,142,255,0.1))', border: '1px solid rgba(124,94,250,0.4)' }}>
            <Calendar size={16} style={{ color: '#a68eff' }} />
          </div>
          <div>
            <div className="font-black text-white text-sm">Einstein Content Plan</div>
            <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>
              Bi-weekly: Sun &amp; Wed at 6:00 PM · Approve, decline, or modify each item
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {pending > 0 && (
            <button onClick={approveAll} data-testid="approve-all-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all hover:opacity-80"
              style={{ background: 'rgba(61,255,160,0.1)', border: '1px solid rgba(61,255,160,0.3)', color: '#3dffa0' }}>
              <Check size={11} /> Approve All ({pending})
            </button>
          )}
          <div className="flex items-center gap-1">
            <select value={days} onChange={e => setDays(Number(e.target.value))}
              className="px-2 py-1.5 rounded-xl text-xs outline-none"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}>
              {[3, 5, 7].map(d => <option key={d} value={d}>{d} days</option>)}
            </select>
            <button onClick={regenerate} disabled={generating} data-testid="regenerate-plan-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(124,94,250,0.12)', border: '1px solid rgba(124,94,250,0.3)', color: '#a68eff' }}>
              {generating ? <RefreshCw size={11} className="animate-spin" /> : <Sparkles size={11} />}
              {generating ? 'Planning...' : 'New Plan'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      {plan && (
        <div className="flex items-center gap-4 px-5 py-2.5 flex-shrink-0"
          style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--llmc-border)' }}>
          {[
            { label: 'Total', count: items.length, color: '#a68eff' },
            { label: 'Pending', count: pending, color: 'rgba(255,255,255,0.4)' },
            { label: 'Approved', count: approved, color: '#3dffa0' },
            { label: 'Declined', count: declined, color: '#fe2c55' },
          ].map(s => (
            <div key={s.label} className="flex items-center gap-1.5">
              <span className="text-sm font-black" style={{ color: s.color }}>{s.count}</span>
              <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{s.label}</span>
            </div>
          ))}
          <div className="ml-auto text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>
            Generated {plan.generated_at ? new Date(plan.generated_at).toLocaleString() : 'today'}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5">
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 space-y-4">
            <RefreshCw size={24} className="animate-spin" style={{ color: '#a68eff' }} />
            <div className="text-sm" style={{ color: 'var(--llmc-text-muted)' }}>Loading Einstein's content plan...</div>
          </div>
        )}

        {!loading && !plan && (
          <div className="flex flex-col items-center justify-center py-20 space-y-5">
            <div className="w-16 h-16 rounded-3xl flex items-center justify-center"
              style={{ background: 'rgba(124,94,250,0.1)', border: '1px solid rgba(124,94,250,0.2)' }}>
              <Brain size={28} style={{ color: '#a68eff' }} />
            </div>
            <div className="text-center">
              <div className="text-sm font-bold text-white mb-2">No plan for today yet</div>
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
                Plans are auto-generated at 7:00 AM. Generate one now to get started.
              </div>
            </div>
            <button onClick={regenerate} disabled={generating} data-testid="generate-first-plan-btn"
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
              {generating ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {generating ? 'Generating...' : `Generate ${days}-Day Plan`}
            </button>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="space-y-3 max-w-3xl mx-auto">
            {items.map(item => (
              <PlanItem key={item.item_id} item={item} onAction={handleAction} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
